// Calendar ranges in an IANA time zone (the browser's, or a fixed one in tests): local days, ISO weeks, months.
import { isoWeekNumber, zoneOffsetMs, zonedMidnight, type Interval } from '../../../packages/core/src/index.ts';

export const MINUTE = 60_000;
export const HOUR = 3_600_000;
export const DAY = 86_400_000;

export interface LocalParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  /** 1 = Monday … 7 = Sunday (ISO). */
  weekday: number;
}

const partFormatters = new Map<string, Intl.DateTimeFormat>();

export function localParts(t: number, timeZone: string): LocalParts {
  let f = partFormatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric', weekday: 'short',
    });
    partFormatters.set(timeZone, f);
  }
  const p: Record<string, string> = {};
  for (const part of f.formatToParts(new Date(t))) p[part.type] = part.value;
  const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  return {
    year: Number(p.year), month: Number(p.month), day: Number(p.day),
    hour: Number(p.hour), minute: Number(p.minute), second: Number(p.second),
    weekday: weekdays.indexOf(p.weekday ?? 'Mon') + 1,
  };
}

/** The instant of a local wall time (the earlier one when it occurs twice; shifted forward inside a gap). */
export function zonedTime(year: number, month: number, day: number, hour: number, minute: number, timeZone: string): number {
  const start = zonedMidnight(year, month, day, timeZone);
  const wall = Date.UTC(year, month - 1, day, hour, minute);
  let t = wall - zoneOffsetMs(wall, timeZone);
  t = wall - zoneOffsetMs(t, timeZone);
  // West of UTC the two steps land before a gap (New York 02:30 as 01:30 EST): move forward by the gap.
  const shown = t + zoneOffsetMs(t, timeZone);
  if (shown < wall) t += wall - shown;
  // East of UTC they land on the later of two occurrences (Berlin 02:30 CET): the offset 14 hours before `wall`, earlier
  // than any instant that can show it, gives the first one.
  const first = wall - zoneOffsetMs(wall - 50_400_000, timeZone);
  if (first < t && first + zoneOffsetMs(first, timeZone) === wall) t = first;
  return Math.max(start, t);
}

export function dayRange(t: number, timeZone: string, offset = 0): Interval {
  const p = localParts(t, timeZone);
  return { start: zonedMidnight(p.year, p.month, p.day + offset, timeZone), end: zonedMidnight(p.year, p.month, p.day + offset + 1, timeZone) };
}

/** The ISO week (Monday to Monday) containing `t`, shifted by `offset` weeks. */
export function weekRange(t: number, timeZone: string, offset = 0): Interval {
  const p = localParts(t, timeZone);
  const monday = p.day - (p.weekday - 1) + offset * 7;
  return { start: zonedMidnight(p.year, p.month, monday, timeZone), end: zonedMidnight(p.year, p.month, monday + 7, timeZone) };
}

export function monthRange(t: number, timeZone: string, offset = 0): Interval {
  const p = localParts(t, timeZone);
  return { start: zonedMidnight(p.year, p.month + offset, 1, timeZone), end: zonedMidnight(p.year, p.month + offset + 1, 1, timeZone) };
}

export function quarterRange(t: number, timeZone: string): Interval {
  const p = localParts(t, timeZone);
  const first = p.month - ((p.month - 1) % 3);
  return { start: zonedMidnight(p.year, first, 1, timeZone), end: zonedMidnight(p.year, first + 3, 1, timeZone) };
}

export function yearRange(t: number, timeZone: string): Interval {
  const p = localParts(t, timeZone);
  return { start: zonedMidnight(p.year, 1, 1, timeZone), end: zonedMidnight(p.year + 1, 1, 1, timeZone) };
}

/** The same local time `days` days later (or earlier). */
export function addDays(t: number, days: number, timeZone: string): number {
  const p = localParts(t, timeZone);
  return zonedTime(p.year, p.month, p.day + days, p.hour, p.minute, timeZone) + p.second * 1000 + (t % 1000);
}

/** Hours since the local midnight of `dayStart` (fractional; a 25-hour day goes up to 25). */
export function hoursInto(t: number, dayStart: number): number {
  return (t - dayStart) / HOUR;
}

/** Shared elapsed-hour bounds; the ticks themselves use each day's actual timestamps (23/25-hour days). */
export function calendarExtent(days: { day: Interval; times: number[] }[], timeZone: string): { first: number; last: number } {
  let first = Infinity;
  let last = 0;
  for (const { day, times } of days) {
    const p = localParts(day.start, timeZone);
    const from = zonedTime(p.year, p.month, p.day, 7, 0, timeZone);
    const to = zonedTime(p.year, p.month, p.day, 20, 0, timeZone);
    first = Math.min(first, Math.floor(hoursInto(from, day.start)));
    last = Math.max(last, Math.ceil(hoursInto(to, day.start)));
    for (const time of times) {
      const hour = hoursInto(Math.max(day.start, Math.min(day.end, time)), day.start);
      first = Math.min(first, Math.floor(hour));
      last = Math.max(last, Math.min((day.end - day.start) / HOUR, Math.floor(hour) + 1));
    }
  }
  first = Number.isFinite(first) ? first : 7;
  return { first, last: Math.max(first + 1, last) };
}

export function weekNumber(t: number, timeZone: string): number {
  return isoWeekNumber(t, timeZone);
}

/** `YYYY-MM-DD` of the local date (file names, date inputs). */
export function isoDate(t: number, timeZone: string): string {
  const p = localParts(t, timeZone);
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

/** `YYYY-MM-DDTHH:MM` for `<input type="datetime-local">`. */
export function isoLocalMinute(t: number, timeZone: string): string {
  const p = localParts(t, timeZone);
  return `${isoDate(t, timeZone)}T${String(p.hour).padStart(2, '0')}:${String(p.minute).padStart(2, '0')}`;
}

/** Parses wall time, retaining an unchanged input's exact original instant; `null` if malformed. */
export function parseLocal(value: string, timeZone: string, original?: number): number | null {
  if (original !== undefined && value === isoLocalMinute(original, timeZone)) return original;
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?$/.exec(value.trim());
  if (!m) return null;
  return zonedTime(Number(m[1]), Number(m[2]), Number(m[3]), Number(m[4] ?? 0), Number(m[5] ?? 0), timeZone);
}

export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Berlin';
  } catch {
    return 'Europe/Berlin';
  }
}
